import {Directions} from '@jetbrains/ring-ui/components/popup/popup.consts'
import Tooltip from '@jetbrains/ring-ui/components/tooltip/tooltip'
import classNames from 'classnames'
import {memo, useCallback, useMemo, useRef} from 'react'

import {useAppDispatch} from '../../../../../hooks/react-redux'
import {useBooleanQueryParam} from '../../../../../hooks/routes'
import CancelChangesButton from '../../components/ChangesButton/Cancel/CancelChangesButton'
import {useSaveChangesState} from '../../components/ChangesButton/hooks/useSaveChangesState'
import SaveChangesButton from '../../components/ChangesButton/Save/SaveChangesButton'
import {useSubmittedProjectList} from '../../components/Feedback/Feedback.hooks'
import {feedbackForm, ShowFeedbackFormReasons} from '../../components/Feedback/Feedback.slice'
import RunButton from '../../components/RunButton/RunButton'
import type {pipelineHeadFragment$key} from '../../hooks/__generated__/pipelineHeadFragment.graphql'
import {usePipelineHeadId, usePipelineId} from '../../hooks/pipeline'
import {useEditPipeline, useSaveYAMLChangesHandler} from '../EditPipelinePage.hooks'

import useSaveChangesError from './hooks/useSaveChangesError'

import styles from './SaveChanges.css'

const READING_TIMEOUT = 5000

type Props = {
  className?: string
  pipelineHeadKey: pipelineHeadFragment$key | null
}

function SaveChanges({className, pipelineHeadKey}: Props) {
  const dispatch = useAppDispatch()
  const pipelineId = usePipelineId()
  const isYaml = useBooleanQueryParam('yaml')
  const timer = useRef<null | number>(null)
  const submittedProjects = useSubmittedProjectList()

  const saveChangesError = useSaveChangesError(pipelineId)
  const editPipeline = useEditPipeline()
  const [saveYAMLChangesHandler] = useSaveYAMLChangesHandler()

  const {hasErrors, hasChanges} = useSaveChangesState()

  const errorJobsLabel = isYaml ? '' : saveChangesError.errorJobsLabel
  const handlerSaveButtonClick = isYaml
    ? saveYAMLChangesHandler
    : editPipeline.handlerSaveButtonClick

  const headId = usePipelineHeadId(pipelineHeadKey)
  const popupProps = useMemo(
    () => ({
      offset: 10,
      autoCorrectTopOverflow: true,
      directions: [Directions.BOTTOM_CENTER],
    }),
    [],
  )

  const onAfterRun = useCallback(() => {
    timer.current != null && clearTimeout(timer.current)

    if (!submittedProjects.includes(pipelineId)) {
      timer.current = window.setTimeout(() => {
        dispatch(
          feedbackForm.actions.setVisibility({
            visible: true,
            reason: ShowFeedbackFormReasons.SAVE_AND_RUN,
          }),
        )

        timer.current != null && clearTimeout(timer.current)
      }, READING_TIMEOUT)
    }
  }, [dispatch, pipelineId, submittedProjects])

  return (
    <div className={classNames(styles.container, className)}>
      <div className={styles.actions}>
        {hasChanges && (
          <>
            <CancelChangesButton className={styles.button} />
            <SaveChangesButton className={styles.button} />
          </>
        )}
        <Tooltip popupProps={popupProps} title={errorJobsLabel}>
          <RunButton
            className={styles.button}
            headId={headId!}
            beforeRun={handlerSaveButtonClick}
            afterRun={onAfterRun}
            disabled={hasErrors}
            primary
          >
            {hasChanges ? 'Save and run' : 'Run'}
          </RunButton>
        </Tooltip>
      </div>
    </div>
  )
}

export default memo(SaveChanges)
